/**
 * Task #1435 — helper FE per i 403 di limite tenant (utenti / agenti AI).
 *
 * Il backend risponde con HTTP 403 + body `{ success:false, code, current, limit, error }`
 * dove `code ∈ {'tenant_user_limit_reached', 'tenant_ai_agent_limit_reached'}`.
 * `apiClient` preserva questo payload su `err.data` (vedi patch in `lib/api.ts`).
 */
import { isApiError, type ApiError } from './api';

export type TenantLimitErrorCode =
  | 'tenant_user_limit_reached'
  | 'tenant_ai_agent_limit_reached';

export interface TenantLimitErrorPayload {
  code: TenantLimitErrorCode;
  current: number;
  limit: number;
  message: string;
}

interface RawLimitErrorData {
  code?: unknown;
  current?: unknown;
  limit?: unknown;
  error?: unknown;
}

function toFiniteInt(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? Math.floor(n) : 0;
}

export function extractTenantLimitError(err: unknown): TenantLimitErrorPayload | null {
  if (!isApiError(err)) return null;
  const apiErr = err as ApiError;
  if (apiErr.status !== 403) return null;
  const data = apiErr.data as RawLimitErrorData | null | undefined;
  const code = data?.code;
  if (code !== 'tenant_user_limit_reached' && code !== 'tenant_ai_agent_limit_reached') {
    return null;
  }
  return {
    code,
    current: toFiniteInt(data?.current),
    limit: toFiniteInt(data?.limit),
    message: typeof data?.error === 'string' ? data.error : apiErr.message,
  };
}

export const TENANT_LIMIT_EVENT = 'api:tenant-limit-reached';

export interface TenantLimitEventDetail extends TenantLimitErrorPayload {}

/**
 * Da chiamare nel `catch` di una creazione utente / agente AI: se l'errore è
 * un limite tenant, il modal globale è già stato aperto dall'apiClient via
 * evento — questa funzione restituisce true così il chiamante può saltare il
 * toast generico "Errore creazione".
 */
export function isTenantLimitHandled(err: unknown): boolean {
  return extractTenantLimitError(err) !== null;
}
